import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { getUser, clearAuth } from '../../utils/auth';
import { stripDr } from '../../utils/format';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import MobileBottomNav from '../../components/MobileBottomNav';
import ThemeToggle from '../../components/ThemeToggle';
import {
  LayoutDashboard,
  Users,
  Calendar,
  MessageSquare,
  FileText,
  ClipboardList,
  Settings,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Stethoscope,
  Clock,
  Star,
  Video,
  Sparkles
} from 'lucide-react';

// Sidebar menu items
const sidebarItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/doctor/dashboard' },
  { icon: Users, label: 'My Patients', path: '/doctor/patients' },
  { icon: Sparkles, label: 'AI Records', path: '/doctor/ai-records' },
  { icon: ClipboardList, label: 'Patient Inquiries', path: '/doctor/inquiries', badge: 'New' },
  { icon: Calendar, label: 'Appointments', path: '/doctor/appointments' },
  { icon: FileText, label: 'My Quotes', path: '/doctor/quotes' },
  { icon: Video, label: 'Video Consultation', path: '/doctor/video-consultation' },
  { icon: MessageSquare, label: 'Messages', path: '/doctor/consultations' },
  { icon: Clock, label: 'Availability', path: '/doctor/availability' },
  { icon: Star, label: 'Reviews', path: '/doctor/reviews' },
  { icon: Settings, label: 'Profile Settings', path: '/doctor/settings' },
];

// Mobile bottom nav items (5 max for clean design)
const mobileNavItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/doctor/dashboard' },
  { icon: Users, label: 'Patients', path: '/doctor/patients', matchPaths: ['/doctor/patients', '/doctor/inquiries'] },
  { icon: Calendar, label: 'Schedule', path: '/doctor/appointments' },
  { icon: Video, label: 'Consults', path: '/doctor/video-consultation' },
  { icon: Settings, label: 'Settings', path: '/doctor/settings' },
];

export default function DoctorLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications] = useState(3);

  // Close sidebar on route change for mobile
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const getInitials = (name) => {
    if (!name) return 'DR';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getPageTitle = () => {
    const item = sidebarItems.find(item => item.path === location.pathname);
    return item?.label || 'Dashboard';
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="doctor-layout">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Desktop */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-72 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800
        transform transition-transform duration-300 ease-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        {/* Sidebar Header */}
        <div className="h-16 flex items-center justify-between px-5 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center shadow-lg shadow-sky-500/20">
              <Stethoscope className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <span className="font-heading font-bold text-slate-900 dark:text-white">Doctor Portal</span>
              <p className="text-xs text-slate-500 dark:text-slate-400">MedRahi</p>
            </div>
          </div>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
        </div>

        {/* Doctor Profile Card */}
        <div className="p-5 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-4">
            <Avatar className="h-14 w-14 ring-2 ring-sky-500/20 ring-offset-2 ring-offset-white dark:ring-offset-slate-900">
              <AvatarImage src={user?.avatar} />
              <AvatarFallback className="bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 font-semibold text-lg">
                {getInitials(user?.full_name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-semibold text-slate-900 dark:text-white truncate">
                Dr. {stripDr(user?.full_name)}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                {user?.specialization || 'Specialist'}
              </p>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Badge className="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800">
              Verified
            </Badge>
            <Badge className="bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 border-sky-200 dark:border-sky-800">
              Active
            </Badge>
          </div>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 h-[calc(100vh-280px)]">
          <nav className="p-4 space-y-1">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium
                    transition-all duration-200
                    ${isActive 
                      ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 shadow-sm' 
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                    }
                  `}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-sky-600 dark:text-sky-400' : ''}`} strokeWidth={1.5} />
                  <span className="flex-1">{item.label}</span>
                  {item.badge && (
                    <Badge className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-[10px] px-2">
                      {item.badge}
                    </Badge>
                  )}
                </NavLink>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Sidebar Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="lg:pl-72">
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800">
          <div className="h-16 px-4 lg:px-8 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                data-testid="mobile-menu-button"
              >
                <Menu className="w-5 h-5 text-slate-600 dark:text-slate-400" strokeWidth={1.5} />
              </button>
              
              {/* Page Title */}
              <div>
                <h1 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">
                  {getPageTitle()}
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 hidden sm:block">
                  Welcome back, Dr. {stripDr(user?.full_name).split(' ')[0]}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Theme Toggle */}
              <ThemeToggle />
              
              {/* Notifications */}
              <button 
                className="relative p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                data-testid="notifications-button"
              >
                <Bell className="w-5 h-5 text-slate-600 dark:text-slate-400" strokeWidth={1.5} />
                {notifications > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-medium rounded-full flex items-center justify-center">
                    {notifications}
                  </span>
                )}
              </button>

              {/* Profile Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 text-sm font-medium">
                        {getInitials(user?.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <ChevronDown className="w-4 h-4 text-slate-400 hidden sm:block" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-heading">Dr. {stripDr(user?.full_name)}</span>
                      <span className="text-xs font-normal text-slate-500">{user?.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/doctor/settings')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Profile Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/doctor/availability')}>
                    <Clock className="w-4 h-4 mr-2" />
                    Manage Availability
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 dark:text-red-400">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8 pb-24 lg:pb-8 min-h-[calc(100vh-4rem)]">
          <Outlet />
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav items={mobileNavItems} baseColor="sky" />
    </div>
  );
}
